import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { DataStats } from "@/components/data-stats/DataStats";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  listCertifications,
  listDuplicateClusters,
  mergeCertifications,
  PaginatedCertificationsResponse,
  DuplicatesResponse,
  DuplicateCluster,
} from "@/services/certificationsApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "@/hooks/use-toast";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

type SortBy = "name" | "authority" | "candidate_count";

function SortHeader<T extends string>({
  label,
  field,
  current,
  order,
  onSort,
}: {
  label: string;
  field: T;
  current: T;
  order: string;
  onSort: (field: T) => void;
}) {
  const active = current === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

function ClusterCard({ cluster, onMerged }: { cluster: DuplicateCluster; onMerged: () => void }) {
  const navigate = useNavigate();
  const [targetId, setTargetId] = useState(cluster.suggested_target_id);
  const [busy, setBusy] = useState(false);

  const doMerge = async (force: boolean) => {
    const sources = cluster.members.filter((m) => m.id !== targetId).map((m) => m.id);
    if (!sources.length) return;
    const target = cluster.members.find((m) => m.id === targetId)!;
    if (!force) {
      const ok = await confirmDialog({
        title: "Fusionner les certifications",
        description: `${sources.length} entrée(s) seront fusionnées dans « ${target.name} ». Les associations candidats sont déplacées; les sources supprimées. Action irréversible.`,
        confirmLabel: "Fusionner",
        destructive: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await mergeCertifications(navigate, { target_id: targetId, source_ids: sources, force });
      if (!res.merged && res.warnings?.length) {
        const ok = await confirmDialog({
          title: "Codes d'examen différents",
          description: `${res.warnings.join(" ; ")}. Forcer la fusion quand même ?`,
          confirmLabel: "Forcer la fusion",
          destructive: true,
        });
        if (ok) { setBusy(false); return doMerge(true); }
      } else if (res.merged) {
        toast({ title: "Fusion effectuée", description: `${res.merged_count} fusionnée(s), ${res.reassigned_links} association(s) déplacée(s).` });
        onMerged();
      }
    } catch (e) {
      toast({ title: "Erreur", description: e instanceof Error ? e.message : "Échec de la fusion", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">
          {cluster.canonical_name ?? cluster.members[0].name}
          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${cluster.auto_mergeable ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
            {cluster.auto_mergeable ? "auto-fusionnable" : cluster.confidence === "catalog-generic" ? "générique — à valider" : "nom — à valider"}
          </span>
        </div>
        <div className="text-xs text-gray-500">{cluster.member_count} entrées · {cluster.total_candidates} candidats</div>
      </div>
      <table className="min-w-full text-sm">
        <tbody>
          {cluster.members.map((m) => (
            <tr key={m.id} className="border-t">
              <td className="px-2 py-1 w-8">
                <input type="radio" name={`tgt-${cluster.suggested_target_id}`} checked={targetId === m.id} onChange={() => setTargetId(m.id)} title="Cible (conserver)" />
              </td>
              <td className="px-2 py-1">{m.name}{targetId === m.id && <span className="ml-2 text-xs text-green-600">cible</span>}</td>
              <td className="px-2 py-1 text-gray-500">{m.authority ?? "—"}</td>
              <td className="px-2 py-1 text-right text-gray-500">{m.candidate_count}c</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end">
        <Button size="sm" disabled={busy} onClick={() => doMerge(false)}>
          {busy ? "Fusion…" : "Fusionner dans la cible"}
        </Button>
      </div>
    </div>
  );
}

export default function CertificationsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<"list" | "duplicates">("list");
  const [data, setData] = useState<PaginatedCertificationsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sortBy, setSortBy] = useState<SortBy>("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dups, setDups] = useState<DuplicatesResponse | null>(null);
  const [dupsLoading, setDupsLoading] = useState(false);
  const [dupFilter, setDupFilter] = useState<"all" | "auto" | "validate">("all");

  useEffect(() => {
    if (view !== "list") return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listCertifications(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder, q: q || undefined });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, view, page, size, sortBy, sortOrder, q]);

  const loadDuplicates = useCallback(async () => {
    setDupsLoading(true);
    setError(null);
    try {
      setDups(await listDuplicateClusters(navigate, { min_candidates: 2, limit: 200 }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la détection");
    } finally {
      setDupsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (view === "duplicates" && !dups && !dupsLoading) loadDuplicates();
  }, [view, dups, dupsLoading, loadDuplicates]);

  const handleSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(field === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Certifications</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Button variant={view === "list" ? "default" : "outline"} size="sm" onClick={() => setView("list")}>Liste</Button>
            <Button variant={view === "duplicates" ? "default" : "outline"} size="sm" onClick={() => setView("duplicates")}>Doublons</Button>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          {view === "list" && (
            <>
              <DataStats entity="certifications" />
              <div className="flex items-center justify-between gap-4">
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(search.trim()); }}
                >
                  <Input placeholder="Rechercher (nom / émetteur)…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" />
                  <Button type="submit" variant="outline" size="sm">Rechercher</Button>
                  {q && <Button type="button" variant="ghost" size="sm" onClick={() => { setSearch(""); setQ(""); setPage(1); }}>Effacer</Button>}
                </form>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Taille page</label>
                  <select className="border rounded px-2 py-1 text-sm" value={size} onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}>
                    {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n}</option>))}
                  </select>
                </div>
              </div>

              {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

              <div className="overflow-auto border rounded">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Id</th>
                      <SortHeader label="Nom" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                      <SortHeader label="Émetteur" field="authority" current={sortBy} order={sortOrder} onSort={handleSort} />
                      <th className="px-3 py-2 text-left">Company</th>
                      <th className="px-3 py-2 text-left">External ref</th>
                      <SortHeader label="Candidats" field="candidate_count" current={sortBy} order={sortOrder} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((s) => (
                      <tr key={`${s.id}-${s.authority ?? ''}`} className="border-t hover:bg-muted/50">
                        <td className="px-3 py-2">{s.id}</td>
                        <td className="px-3 py-2 font-medium">{s.name}</td>
                        <td className="px-3 py-2">{s.authority ?? "—"}</td>
                        <td className="px-3 py-2">
                          {s.issuing_company ? (
                            <a href={`/companies/${s.issuing_company.id}`} className="text-blue-600 hover:underline">{s.issuing_company.name}</a>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 font-mono">{s.external_ref ?? "—"}</td>
                        <td className="px-3 py-2">{s.candidate_count}</td>
                      </tr>
                    ))}
                    {!data?.items?.length && !loading && (
                      <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={6}>Aucune certification</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements</div>
                <div className="flex gap-2">
                  <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
                  <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
                </div>
              </div>
            </>
          )}

          {view === "duplicates" && (
            <>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {dups ? `${dups.cluster_count} groupes de doublons · ${dups.mergeable_rows} entrées · ${dups.candidates_affected} candidats concernés` : "—"}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex rounded border overflow-hidden text-sm">
                    {([["all", "Tous"], ["auto", "Auto-fusionnables"], ["validate", "À valider"]] as const).map(([k, label]) => (
                      <button key={k} onClick={() => setDupFilter(k)}
                        className={`px-2 py-1 ${dupFilter === k ? "bg-gray-800 text-white" : "bg-white hover:bg-gray-100"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" disabled={dupsLoading} onClick={loadDuplicates}>{dupsLoading ? "Analyse…" : "Recharger"}</Button>
                </div>
              </div>
              {dupsLoading && <div className="text-gray-500 text-sm">Détection des doublons…</div>}
              <div className="space-y-3">
                {dups?.clusters
                  ?.filter((c) => dupFilter === "all" || (dupFilter === "auto" ? c.auto_mergeable : !c.auto_mergeable))
                  .map((c) => (
                    <ClusterCard key={`${c.confidence}-${c.suggested_target_id}`} cluster={c} onMerged={loadDuplicates} />
                  ))}
                {dups && !dups.clusters.length && !dupsLoading && (
                  <div className="text-center text-gray-500 text-sm py-6">Aucun doublon détecté</div>
                )}
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
